import Dropdown from 'bootstrap/js/src/dropdown';
import Modal from 'bootstrap/js/src/modal';
import Offcanvas from 'bootstrap/js/src/offcanvas';
import Tooltip from 'bootstrap/js/src/tooltip';
import { MODAL_CLOSE_CONFIRMATION_SKIP_ONCE } from './modal_close_confirmation_attributes';

/**
 * To make sure Bootstrap components get restored properly
 * when Turbo loads a page from cache, we need to clean up
 * Bootstrap's JavaScript components properly
 */

document.addEventListener('turbo:before-cache', function () {
  hideOpenModalAndOffcanvas();
  hideOpenDropdowns();
  hideVisibleTooltips();
});

// Modals & Offcanvas'

// Runs whichever completion Bootstrap has queued on `el` right now, synchronously.
// `executeAfterTransition` waits on a `transitionend` listener that removes itself
// as it runs, so this is a no-op when nothing is pending.
function flushPendingTransition(el) {
  el.dispatchEvent(new Event('transitionend'));
}

// Both overlays must be *fully* hidden by the time this returns, with nothing
// left queued. Bootstrap defers the work that ends a hide — the part that sets
// `visibility: hidden` — behind a transition, and a deferred completion outlives
// the navigation: it lands on the next page, where it can blind a panel the user
// has already reopened, leaving `.show` on an invisible element that can never be
// seen again. The two overlays need different treatment to get there.
function hideOpenModalAndOffcanvas() {
  // Bootstrap guarantees only one of each element will be open so we can just `querySelector`
  const openElements = [
    document.querySelector('.modal.show'),
    document.querySelector('.offcanvas.show'),
  ].filter((el) => el);

  openElements.forEach((el) => {
    const isModal = el.classList.contains('modal');
    const instance = isModal
      ? Modal.getInstance(el)
      : Offcanvas.getInstance(el);

    if (!instance) return;

    if (isModal) {
      // Modal#hide() asks `_isAnimated()` whether to wait, and that reads the
      // `fade` class — so removing it makes the hide and its completion run
      // synchronously.
      // https://github.com/twbs/bootstrap/blob/edf9c40956d19e6ab3f9151bfe0dfac6be06fa21/js/src/modal.js#L256
      el.classList.remove('fade');

      // Signal to modal_close_confirmation_controller that this hide is
      // programmatic (Turbo caching) and should skip the confirmation prompt
      el.setAttribute(MODAL_CLOSE_CONFIRMATION_SKIP_ONCE, 'true');
    }

    // Offcanvas#hide() never asks: it passes `isAnimated: true` unconditionally
    // (offcanvas.js `_queueCallback(completeCallback, this._element, true)`) and
    // reads the `fade` class nowhere, so the trick above cannot work here — an
    // offcanvas hide is always deferred. `executeAfterTransition` has exactly one
    // synchronous path and it is unreachable from here, so the only way to finish
    // the hide now is to give Bootstrap the `transitionend` it is waiting for.
    //
    // Twice, though, because that dispatch is not addressed to one listener:
    // every queued completion adds its own `transitionend` listener to the same
    // element, and `hide()` does not remove the one `show()` left behind. Hiding
    // an offcanvas whose show is still in flight would therefore run the stale
    // show completion first — which calls `_focustrap.activate()`, undoing the
    // `deactivate()` that `hide()` had just done in its body — and leave a hidden
    // panel holding the keyboard focus trap. So drain whatever is pending before
    // hiding (settling an in-flight show, or doing nothing at all), then hide,
    // then run that hide's own completion. Each listener removes itself as it
    // runs, so the second dispatch can only ever resolve the hide.
    if (!isModal) flushPendingTransition(el);

    instance.hide();

    if (!isModal) flushPendingTransition(el);

    // Bootstrap builds a backdrop per show and tears it down behind a transition
    // of its own, so detach it here rather than let that land after the
    // navigation. Its pending teardown then only re-detaches a detached node,
    // which is inert.
    document.querySelector('.offcanvas-backdrop')?.remove();
    document.querySelector('.modal-backdrop')?.remove();

    // Restore `fade` to ensure transitions happen as intended when Turbo restores
    // the page. Only the modal ever had it: adding it to an offcanvas, which
    // never carries the class, left a stray `fade` behind on every navigation.
    if (isModal) el.classList.add('fade');
  });
}

// Dropdowns

function hideOpenDropdowns() {
  const openDropdownElements = document.querySelectorAll(
    '.dropdown-toggle.show'
  );
  for (const openDropdownElement of openDropdownElements) {
    hideOpenDropdown(openDropdownElement);
  }
}

function hideOpenDropdown(element) {
  const dropdown = Dropdown.getInstance(element);
  if (dropdown) {
    dropdown.hide();
  }
}

// Tooltips

function hideVisibleTooltips() {
  const visibleTooltipElements = document.querySelectorAll('.tooltip');
  for (const visibleTooltipElement of visibleTooltipElements) {
    hideVisibleTooltip(visibleTooltipElement);
  }
}

function hideVisibleTooltip(element) {
  const toggle = document.querySelector(`[aria-describedby="${element.id}"]`);
  const instance = Tooltip.getInstance(toggle);
  // There seems to be click timings where there'd be no instance
  if (instance) {
    element.classList.remove('fade');
    instance.hide();
    element.classList.add('fade');
  }
}
